<template>
  <div id="validation-btn">
    <div v-if="errors.length === 0" class="mx-2">
      <Button class="mx-2" icon="pi pi-check-circle" @click.stop="toggle" />
    </div>

    <div v-else-if="hasCatastrophicError" class="mx-2 badge-wrapper">
      <Button class="p-button-info" @click.stop="toggle">🔥</Button>
      <Badge severity="danger" class="p-overlay-badge" value="!!!" />
    </div>
    <div v-else class="mx-2 badge-wrapper">
      <Button icon="pi pi-bell" @click.stop="toggle" />
      <Badge severity="danger" class="p-overlay-badge" :value="errors.length" />
    </div>
  </div>
  <OverlayPanel
    v-if="isMounted"
    ref="op"
    append-to="body"
    :show-close-icon="true"
  >
    <div class="panel">
      <p v-if="errors.length === 0" class="text-center py-4 no-error">
        No Errors Found
      </p>
      <div
        v-for="error in errors"
        :key="error.message"
        class="message flex p-2 my-2 align-items-center"
        :class="severity[error.severity]"
        @click="locateEquipment(error, $event)"
      >
        <icon
          :icon="['fas', 'exclamation-triangle']"
          size="lg"
          class="icon ml-1 mr-3"
        />
        <p>{{ error.message }}</p>
        <div v-if="error.resolver" class="flex pl-4">
          <Button
            size="small"
            raised
            rounded
            @click.stop="error.resolver?.(parametersForm)"
          >
            Resolve
          </Button>
        </div>
      </div>
    </div>
  </OverlayPanel>
</template>

<script setup lang="ts">
// import { scenarioValidatorKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import type { ValidationRule } from "@/composables/validation/scenarioValidator";
import { Severity } from "@/composables/validation/scenarioValidator";
import { injectStrict } from "@/utils/utils";
import { computed, nextTick, onMounted, ref } from "vue";
import {
  parametersKey,
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey,
  wellPropertiesKey,
  scenarioValidatorKey,
  parametersFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useEquipmentCalculatorSections } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { useAnchoredOverlay } from "@/composables/ui/useAnchoredOverlay";
// import { useStackValidator } from "@/composables/scenarioDetailProvider/useStackValidator";
// import { useHasDualStacks } from "@/composables/scenarioDetailProvider/stackFiltering";
import { useRouter } from "vue-router";

const parametersForm = injectStrict(parametersFormKey);

const scenarioCalculations = injectStrict(scenarioCalculationsKey);
const parameters = injectStrict(parametersKey);
const wellProperties = injectStrict(wellPropertiesKey);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
// const equipment = injectStrict(equipmentKey);

const equipmentCalculatorSections = useEquipmentCalculatorSections(
  scenarioCalculations,
  parameters,
  wellProperties,
  scenario,
  sizing
);

const router = useRouter();

const op = ref();
const { errors } = injectStrict(scenarioValidatorKey);
// const hasDualStacks = useHasDualStacks(equipment);

// // Use stack-aware validator when dual stacks are enabled, otherwise use legacy validator
// const stackValidators = computed(() => {
//   if (hasDualStacks.value) {
//     return useStackValidator("both");
//   }
//   return null;
// });
// const legacyValidator = computed(() => {
//   if (!hasDualStacks.value) {
//     return injectStrict(scenarioValidatorKey);
//   }
//   return null;
// });

// // Combine errors from both stacks when dual stacks are enabled
// const errors = computed(() => {
//   if (hasDualStacks.value && stackValidators.value) {
//     const validators = stackValidators.value;
//     // Type guard: check if it's the object with upper/lower
//     if ("upper" in validators && "lower" in validators) {
//       const upperErrors = validators.upper.errors.value;
//       const lowerErrors = validators.lower.errors.value;
//       // Combine and deduplicate by message (in case same error appears in both)
//       const allErrors = [...upperErrors, ...lowerErrors];
//       // Deduplicate by message and affectedXrefIds
//       const uniqueErrors = allErrors.filter(
//         (error, index, self) =>
//           index ===
//           self.findIndex(
//             e =>
//               e.message === error.message &&
//               JSON.stringify(e.affectedXrefIds) ===
//                 JSON.stringify(error.affectedXrefIds)
//           )
//       );
//       return uniqueErrors;
//     }
//   }
//   return legacyValidator.value?.errors.value || [];
// });

const severity = ["success", "info", "warn", "error", "catastrophe"];

function getAlertsOverlayElement() {
  const overlays = Array.from(
    document.querySelectorAll<HTMLElement>(".p-overlaypanel")
  );

  const isVisible = (el: HTMLElement) => {
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }
    return el.getClientRects().length > 0;
  };

  return (
    overlays.find(
      overlay => overlay.querySelector(".panel") && isVisible(overlay)
    ) ??
    overlays.find(overlay => overlay.querySelector(".panel")) ??
    null
  );
}

const { scheduleSync: scheduleOverlaySync } = useAnchoredOverlay({
  getOverlayElement: getAlertsOverlayElement,
  getAnchorElement: () =>
    document.querySelector<HTMLElement>("#validation-btn button"),
  viewportPadding: 8,
  verticalGap: 10
});

const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});

let latestLocateToken = 0;
let activeCorrectionToken = 0;

function getTopOcclusionHeight() {
  const centerX = window.innerWidth / 2;
  const topScanLimit = Math.min(window.innerHeight * 0.6, 420);
  const elements = Array.from(document.querySelectorAll<HTMLElement>("*"));
  const topCovering = elements
    .filter(el => {
      const style = window.getComputedStyle(el);
      if (style.position !== "sticky" && style.position !== "fixed") {
        return false;
      }

      const rect = el.getBoundingClientRect();
      if (rect.height <= 0 || rect.width <= 0) {
        return false;
      }

      const intersectsTopRegion = rect.top < topScanLimit && rect.bottom > 0;
      const spansCenterX = rect.left <= centerX && rect.right >= centerX;
      const isLikelyHeader = rect.height < window.innerHeight * 0.7;

      return intersectsTopRegion && spansCenterX && isLikelyHeader;
    })
    .map(el => Math.max(0, el.getBoundingClientRect().bottom));

  return topCovering.length ? Math.max(...topCovering) : 0;
}

function pickScrollTarget(
  sectionHash: string,
  rulePlacement: EquipmentCalculatorSectionKey
) {
  const anchor = document.querySelector<HTMLElement>(`#${sectionHash}`);
  if (!anchor) {
    return null;
  }

  const section = anchor.closest(".equipment-row");
  if (!section) {
    return anchor;
  }

  const panelHeaders = Array.from(
    section.querySelectorAll<HTMLElement>("[data-cy='equipment-panel-header']")
  );

  const byHeaderText = (text: string) =>
    panelHeaders.find(header =>
      (header.textContent || "").toLowerCase().includes(text)
    );

  if (rulePlacement === EquipmentCalculatorSectionKey.MLE) {
    const mleInputs = section.querySelector<HTMLElement>(
      ".mle-inputs-container"
    );
    if (mleInputs) {
      return mleInputs;
    }

    const mleSearchView = section.querySelector<HTMLElement>(
      "[data-cy='mle-search-view']"
    );
    if (mleSearchView) {
      return mleSearchView;
    }

    const mleHeader = byHeaderText("mle");
    const mlePanel = mleHeader?.closest<HTMLElement>(
      ".equipment-panel, [data-cy='equipment-row']"
    );
    if (mlePanel) {
      return mlePanel;
    }
  }

  if (rulePlacement === EquipmentCalculatorSectionKey.SEAL) {
    const sealHeader = byHeaderText("seal") || byHeaderText("thrust");
    const sealPanel = sealHeader?.closest<HTMLElement>(".equipment-panel");
    if (sealPanel) {
      return sealPanel;
    }
  }

  if (rulePlacement === EquipmentCalculatorSectionKey.CABLE) {
    const cableHeader = byHeaderText("cable");
    const cablePanel = cableHeader?.closest<HTMLElement>(".equipment-panel");
    if (cablePanel) {
      return cablePanel;
    }
  }

  const panelTarget = section.querySelector<HTMLElement>(
    ".equipment-panel.catastrophe, .equipment-panel.error, .equipment-panel.warn"
  );
  if (panelTarget) {
    return panelTarget;
  }

  const rowTarget = section.querySelector<HTMLElement>(
    "[data-cy$='row-section'], [data-cy='equipment-row']"
  );
  return rowTarget ?? anchor;
}

function scrollTargetIntoView(
  target: HTMLElement,
  rulePlacement: EquipmentCalculatorSectionKey
) {
  activeCorrectionToken += 1;
  const correctionToken = activeCorrectionToken;

  const baseGap = 20;
  const placementGap =
    rulePlacement === EquipmentCalculatorSectionKey.MLE ? 56 : baseGap;
  const topOcclusion = getTopOcclusionHeight();
  const top =
    target.getBoundingClientRect().top +
    window.scrollY -
    topOcclusion -
    placementGap;
  window.scrollTo({ top, behavior: "auto" });

  // Sticky headers can resize while scrolling; keep correcting briefly
  // so the target does not remain tucked under the header.
  const correctIfHidden = () => {
    const refreshedOcclusion = getTopOcclusionHeight();
    const currentTop = target.getBoundingClientRect().top;
    const minVisibleTop = refreshedOcclusion + placementGap;
    if (currentTop < minVisibleTop) {
      const delta = minVisibleTop - currentTop;
      window.scrollBy({ top: -delta, behavior: "auto" });
      return true;
    }
    return false;
  };

  const startedAt = performance.now();
  const maxCorrectionMs = 650;
  let cancelledByUser = false;

  const cancelCorrection = () => {
    cancelledByUser = true;
  };

  const cleanupListeners = () => {
    window.removeEventListener("wheel", cancelCorrection);
    window.removeEventListener("touchstart", cancelCorrection);
    window.removeEventListener("keydown", cancelCorrection);
    window.removeEventListener("mousedown", cancelCorrection);
  };

  window.addEventListener("wheel", cancelCorrection, { passive: true });
  window.addEventListener("touchstart", cancelCorrection, { passive: true });
  window.addEventListener("keydown", cancelCorrection);
  window.addEventListener("mousedown", cancelCorrection);

  const runCorrection = () => {
    if (correctionToken !== activeCorrectionToken || cancelledByUser) {
      cleanupListeners();
      return;
    }

    const corrected = correctIfHidden();
    const elapsed = performance.now() - startedAt;

    if (elapsed < maxCorrectionMs && corrected) {
      requestAnimationFrame(runCorrection);
    } else if (elapsed < maxCorrectionMs && !corrected) {
      // Keep checking briefly for sticky-header transitions.
      requestAnimationFrame(runCorrection);
    } else {
      cleanupListeners();
    }
  };

  requestAnimationFrame(runCorrection);
}

async function ensureScrollToSection(
  sectionHash: string,
  rulePlacement: EquipmentCalculatorSectionKey,
  locateToken: number,
  timeout = 1800,
  delay = 60
) {
  for (let remaining = timeout; remaining > 0; remaining -= delay) {
    if (locateToken !== latestLocateToken) {
      return;
    }

    const target = pickScrollTarget(sectionHash, rulePlacement);
    if (locateToken !== latestLocateToken) {
      return;
    }

    if (target) {
      if (locateToken !== latestLocateToken) {
        return;
      }
      scrollTargetIntoView(target, rulePlacement);
      return;
    }

    await new Promise(resolve => setTimeout(resolve, delay));
    if (locateToken !== latestLocateToken) {
      return;
    }
  }
}

function locateEquipment(err: ValidationRule, event: MouseEvent) {
  event.stopPropagation();

  const location = err.rulePlacement;
  const section = equipmentCalculatorSections.value.find(
    s => s.id === location
  );

  if (section) {
    latestLocateToken += 1;
    const locateToken = latestLocateToken;
    op.value?.hide?.();

    router
      .push({
        name: section.routeName
      })
      .finally(() => {
        nextTick(() => {
          ensureScrollToSection(section.hash, location, locateToken);
        });
      });
  } else {
    console.warn(`Section with id not found.`);
  }
}

const hasCatastrophicError = computed(() =>
  errors.value.some(error => error.severity === Severity.catastrophe)
);

const toggle = (event: MouseEvent) => {
  // Invalidate any in-flight locate request when reopening alerts.
  latestLocateToken += 1;
  op.value && op.value?.toggle(event);

  nextTick(scheduleOverlaySync);
};
</script>

<style lang="scss" scoped>
.badge-wrapper {
  position: relative;
}
.p-overlay-badge {
  transform: translate(-50%, -50%);
  z-index: 10;
  position: absolute;
}
.panel {
  max-width: 500px;
  max-height: 400px;
  overflow-y: auto;
  color: var(--gray-900);
}
.message {
  border-radius: 5px;
  line-height: 1.4rem;
}
.info,
.warn,
.error,
.catastrophe {
  cursor: pointer;
}
.info {
  background-color: var(--gray-500);
  .icon {
    color: var(--gray-600);
  }
}
.warn {
  background-color: var(--warning-100);
  color: black;
  .icon {
    color: var(--warning-700);
  }
}
.error {
  background-color: var(--danger-100);
  color: black;
  .icon {
    color: var(--danger-500);
  }
}
.catastrophe {
  background-color: var(--danger-400);
  color: white;
}
[data-theme="dark"] {
  .no-errors {
    color: #ffffff;
  }
}
[data-theme="light"] {
  .no-errors {
    color: #000000;
  }
}
</style>
